import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { AriaOrb } from "../components/AriaOrb";
import { Save, Play } from "lucide-react";

const VOICES = ["nova", "alloy", "echo", "fable", "onyx", "shimmer"];

export default function AriaSettings() {
  const { refreshOrg } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [audio, setAudio] = useState(null);

  useEffect(() => {
    api.get("/settings/aria").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  if (!cfg) return <div className="p-8">Loading…</div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    try {
      const { data } = await api.put("/settings/aria", {
        aria_nickname: cfg.aria_nickname,
        system_prompt: cfg.system_prompt,
        response_style: cfg.response_style,
        tts_voice: cfg.tts_voice,
        tts_speed: cfg.tts_speed,
        max_tokens: cfg.max_tokens,
        accent_color: cfg.accent_color,
        welcome_message: cfg.welcome_message,
        logo_url: cfg.logo_url,
      });
      setCfg(data);
      await refreshOrg();
      toast.success("ARIA settings saved");
    } catch (e) { toast.error("Save failed"); }
  };

  const previewVoice = async () => {
    try {
      const { data } = await api.post("/tts", {
        text: `Hello, I'm ${cfg.aria_nickname}. ${cfg.welcome_message}`,
        voice: cfg.tts_voice, speed: cfg.tts_speed,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setAudio(url);
      setTimeout(() => new Audio(url).play(), 50);
    } catch { toast.error("Preview failed"); }
  };

  return (
    <div className="h-screen overflow-y-auto p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl mb-1">ARIA Configuration</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Tune persona, prompt, voice, and branding.</p>
          </div>
          <div className="flex items-center gap-3">
            <AriaOrb size={48} color={cfg.accent_color} />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="aria-card p-6">
            <h3 className="font-serif text-lg mb-4">Persona & voice</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Persona name</label>
                <input value={cfg.aria_nickname} onChange={e=>set("aria_nickname", e.target.value)}
                  data-testid="cfg-nickname"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Welcome message</label>
                <textarea rows={3} value={cfg.welcome_message} onChange={e=>set("welcome_message", e.target.value)}
                  data-testid="cfg-welcome"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Response style</label>
                <div className="flex gap-2 mt-1">
                  {["voice", "text"].map(s => (
                    <button key={s} onClick={()=>set("response_style", s)}
                      data-testid={`cfg-style-${s}`}
                      className={`px-3 py-1.5 text-sm rounded-md border ${cfg.response_style === s ? "bg-[#2563FF] text-white border-[#2563FF]" : "border-[var(--aria-border)]"}`}>
                      {s === "voice" ? "Voice-optimised" : "Text-formatted"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">TTS voice</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {VOICES.map(v => (
                    <button key={v} onClick={()=>set("tts_voice", v)} data-testid={`voice-${v}`}
                      className={`px-3 py-1 text-xs rounded-full border ${cfg.tts_voice === v ? "bg-[#18181B] text-white border-[#18181B]" : "border-[var(--aria-border)]"}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">TTS speed: {(cfg.tts_speed ?? 1.22).toFixed(2)}x  ·  <span className="text-[var(--aria-primary)]">faster = snappier JARVIS feel</span></label>
                <input type="range" min="0.85" max="1.5" step="0.01"
                  value={cfg.tts_speed ?? 1.22} onChange={e=>set("tts_speed", parseFloat(e.target.value))}
                  data-testid="cfg-speed"
                  className="w-full mt-2 accent-[#2563FF]" />
                <div className="flex justify-between text-[10px] font-mono text-[var(--aria-text-faint)] mt-1 tracking-wider">
                  <span>0.85 SLOW</span>
                  <span>1.0 NORMAL</span>
                  <span>1.22 SNAPPY</span>
                  <span>1.5 RAPID</span>
                </div>
              </div>
              <button onClick={previewVoice} data-testid="preview-voice-btn"
                className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-[var(--aria-border)] hover:bg-slate-50 dark:hover:bg-white/5">
                <Play size={14} /> Preview voice
              </button>
            </div>
          </div>

          <div className="aria-card p-6">
            <h3 className="font-serif text-lg mb-4">Behavior & branding</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">System prompt</label>
                <textarea rows={7} value={cfg.system_prompt} onChange={e=>set("system_prompt", e.target.value)}
                  data-testid="cfg-prompt"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF] font-mono text-xs" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Max response tokens: {cfg.max_tokens}</label>
                <input type="range" min="100" max="2000" step="50" value={cfg.max_tokens} onChange={e=>set("max_tokens", parseInt(e.target.value))}
                  data-testid="cfg-max-tokens"
                  className="w-full mt-2 accent-[#2563FF]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Accent color</label>
                <div className="flex items-center gap-3 mt-1">
                  <input type="color" value={cfg.accent_color} onChange={e=>set("accent_color", e.target.value)}
                    data-testid="cfg-accent"
                    className="w-12 h-10 rounded-md border border-[var(--aria-border)]" />
                  <input value={cfg.accent_color} onChange={e=>set("accent_color", e.target.value)}
                    className="flex-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[var(--aria-text-muted)]">Logo URL (optional)</label>
                <input value={cfg.logo_url || ""} onChange={e=>set("logo_url", e.target.value)}
                  placeholder="https://your-cdn.com/logo.png"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none focus:ring-2 focus:ring-[#2563FF]" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={save} data-testid="save-aria-cfg"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE] font-medium">
            <Save size={16} /> Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
